import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronDown, ChevronUp } from 'lucide-react';
import { useProfileStore } from '../../stores/profileStore';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { ThemeToggleButton } from '../common/ThemeToggleButton';
import ProfileAvatar from '../common/ProfileAvatar';
import LanguageSwitcher from '../LanguageSwitcher';

interface AdminDesktopNavigationProps {
  isAccountMenuOpen: boolean;
  onSignOut: () => void;
  onAccountMenuToggle: (forceClose?: boolean) => void;
  onCloseMenu: () => void;
  notifications: number;
}

export const AdminDesktopNavigation: React.FC<AdminDesktopNavigationProps> = ({
  isAccountMenuOpen,
  onSignOut,
  onAccountMenuToggle,
  onCloseMenu,
  notifications,
}) => {
  const { profile } = useProfileStore();
  const { t } = useTranslation('common');
  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`;

  return (
    <div className="hidden w-full items-center justify-between px-5 md:flex">
      <div className="flex flex-shrink-0 items-center justify-end">
        <div
          className={`flex flex-col items-center gap-4 relative p-2 ${
            isAccountMenuOpen ? 'bg-white border-2xl rounded-tl-3xl rounded-tr-3xl' : ''
          } ${profile ? '' : 'hidden'}`}
        >
          <div className="relative flex items-center gap-3">
            <Link
              to="/admin/notifications"
              className="relative flex h-11 w-11 items-center justify-center rounded-full transition hover:bg-gray-200"
            >
              <Bell className="h-5 w-5 text-secondary" />
              {notifications > 0 && (
                <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1.5 py-0.5 text-xs text-lightest">
                  {notifications}
                </span>
              )}
            </Link>

            <LanguageSwitcher />

            <ThemeToggleButton />

            <div className="flex items-center gap-2">
              <span className="font-ambit font-normal text-secondary">{profile?.first_name}</span>
              <span className="font-ambit font-bold text-secondary">{profile?.last_name}</span>
              <span>
                {isAccountMenuOpen ? (
                  <ChevronDown className="h-4 w-4 text-secondary" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-secondary" />
                )}
              </span>
              <button
                className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-transparent bg-[#EAF1F7] font-bold text-secondary transition hover:border-primary focus:outline-none"
                onClick={() => onAccountMenuToggle()}
                aria-haspopup="true"
                aria-expanded={isAccountMenuOpen}
              >
                <ProfileAvatar
                  profile_id={profile?.id}
                  first_name={profile?.first_name ?? ''}
                  avatar_url={profile?.avatar_url}
                  initials={initials}
                  className="h-full w-full min-h-0 min-w-0"
                />
              </button>
            </div>
          </div>

          {isAccountMenuOpen && (
            <div className="border-2xl animate-fade-in absolute right-0 top-12 z-40 flex w-full flex-col gap-2 rounded-bl-3xl rounded-br-3xl bg-white p-3">
              <Link
                to="admin/settings"
                className="w-full rounded-xl py-2 text-center text-[17px] font-bold text-secondary hover:bg-primary-inverse"
                onClick={() => onCloseMenu()}
              >
                {t('accountCard.settings')}
              </Link>
              <Button
                className="mt-14 w-full text-[17px] transition"
                variant="primary"
                onClick={onSignOut}
              >
                {t('accountCard.signOut')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
